'use client'
import { Dispatch, SetStateAction, memo, useEffect, useState } from 'react'
import CustomDrawerModal from '../common/CustomDrawerModal'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { consumerRequest } from '@/helper/request'
import { GET_PRODUCT_CONSTITUENTS_API } from '@/helper/apiConstants'
import useCustomToast from '@/hooks/useCustomToast'
import { X } from 'lucide-react'

interface ProductParametersModalProps {
  // Callers read this off an `any` product payload (TestCard passes
  // PRODUCT_DETAIL?.id), so it genuinely can be absent.
  productId?: number
  open: boolean
  onOpenChange: Dispatch<SetStateAction<boolean>>
}

interface ConstituentsData {
  productName: string
  totalTestIncludedCount: number
  testsIncluded: Array<{
    name: string
    parameterCount: number
    elementsIncluded: Array<{
      parameterName: string
    }>
  }>
}

// Cache constituents data globally across all modal instances
const constituentsCache = new Map<number, ConstituentsData>()

const ProductParametersModal = memo(({ productId, open, onOpenChange }: ProductParametersModalProps) => {
  const [constituentsData, setConstituentsData] = useState<ConstituentsData | null>(null)
  const [loadingParameters, setLoadingParameters] = useState(false)
  const showToast = useCustomToast()

  useEffect(() => {
    if (!open || !productId) return

    // Check cache first
    const cached = constituentsCache.get(productId)
    if (cached) {
      setConstituentsData(cached)
      return
    }

    // Fetch data if not in cache
    const fetchParameters = async () => {
      setLoadingParameters(true)
      try {
        const response: any = await consumerRequest(
          GET_PRODUCT_CONSTITUENTS_API,
          'post',
          { productId }
        )

        if (response?.status === true && response?.data) {
          const data = response.data as ConstituentsData
          // Cache the data
          constituentsCache.set(productId, data)
          setConstituentsData(data)
        } else {
          showToast('danger', 'Failed to load parameters')
        }
      } catch (error) {
        console.error('Error fetching parameters:', error)
        showToast('danger', 'Error loading parameters')
      } finally {
        setLoadingParameters(false)
      }
    }

    fetchParameters()
  }, [open, productId, showToast])

  if (!open) return null

  return (
    <CustomDrawerModal
      open={open}
      onOpenChange={onOpenChange}
      maxWidth={848}
      dialogClassName="p-0"
      dialogContentClassName="mt-0 rounded-none h-[100dvh] max-h-[100dvh] md:h-auto md:max-h-[80vh] md:rounded-lg md:mt-0"
      showCloseButton={false}
    >
      <div className="flex flex-col h-[100dvh] md:h-[80vh]">
        <div className="flex-shrink-0 px-4 py-4 md:p-4 border-b border-gray-200 flex items-start justify-between gap-3">
          {loadingParameters ? (
            <div className="animate-pulse flex-1 min-w-0">
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ) : (
            <div className="flex-1 min-w-0">
              <h2 className="text-body-lg-medium sm:text-heading-md text-gray-900 mb-1 truncate">
                {constituentsData?.productName}
              </h2>
              <p className="text-body-sm-medium text-primary-500">
                {constituentsData?.totalTestIncludedCount
                  ? `Contains ${constituentsData.totalTestIncludedCount} tests`
                  : 'Loading...'}
              </p>
            </div>
          )}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close parameters"
            className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"
          >
            <X size={18} className="text-gray-800" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-2 py-4 md:p-4">
          {loadingParameters ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse bg-gray-50 rounded-xl p-4 h-14"></div>
              ))}
            </div>
          ) : (
            <Accordion type="multiple" className="bg-white-100 rounded-[12px] space-y-3">
              {/* The response is cast to ConstituentsData without validation, so
                  testsIncluded is `any` wearing a type - ?. would not save a
                  non-array here. */}
              {(Array.isArray(constituentsData?.testsIncluded)
                ? constituentsData.testsIncluded
                : []
              ).map((test, idx) => (
                <AccordionItem
                  key={idx}
                  value={`test-${idx}`}
                  className="border-none rounded-xl bg-gray-50 px-4"
                >
                  <AccordionTrigger className="hover:no-underline py-3">
                    <div className="flex items-start gap-2 sm:gap-3 w-full text-left">
                      {idx + 1}.

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-label-md sm:text-label-lg">
                            <span className="text-gray-900">{test?.name}</span>
                            <span className="text-gray-500 font-normal"> &mdash; {test?.parameterCount} parameter{test?.parameterCount !== 1 ? 's' : ''}</span>
                          </h3>
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="px-1 pb-3 bg-gray-50">
                    <div className="space-y-2">
                      {(Array.isArray(test?.elementsIncluded)
                        ? test.elementsIncluded
                        : []
                      ).map((element, i) => (
                        <div key={i} className="flex items-center gap-2 rounded-md">
                          <div className="w-1.5 h-1.5 bg-gray-600 rounded-full"></div>
                          <p className="text-body-sm text-gray-600">
                            {element?.parameterName}
                          </p>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </div>
    </CustomDrawerModal>
  )
})

ProductParametersModal.displayName = 'ProductParametersModal'

export default ProductParametersModal
